import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { GanttChartComponent } from "src/app/components/shared/charts/gantt-chart/gantt-chart.component";
import * as d3 from "d3";

/** Default widgets config for embedded gantt in Scraii AI (matches production fallbacks). */
export const SCRAII_GANTT_WIDGETS_CONFIG = {
  config: {
    circle_flags: { controls: "yes" },
    months_visible: 6,
    headerAccess: { name: "Yes" },
    trans_popup: { name: "Yes" },
    defaultExpend: 2,
    tree_color_config: {
      values: [
        { col_value: "HUB", color: "#ffaf4a" },
        { col_value: "SP", color: "#6332CA" },
        { col_value: "default", color: "#6332CA" },
      ],
    },
    legend_color_config: {
      values: [
        { col_value: "dispense", color: "#8C23E6" },
        { col_value: "nurse call", color: "#E223E6" },
        { col_value: "copay", color: "#FFCD4A" },
        { col_value: "change", color: "#1468E1" },
        { col_value: "free goods", color: "red" },
      ],
    },
    program_color_config: {
      values: [
        { col_value: "dispense", color: "#4AA8FF" },
        { col_value: "defualt", color: "#1468E1" },
      ],
    },
    brand_color_config: {
      values: [
        { col_value: "Jakafi", color: "#1468E1" },
        { col_value: "Rezurock", color: "#8C23E6" },
        { col_value: "AXA", color: "#FF964A" },
      ],
    },
  },
};

/**
 * Thin wrapper around the shared app-gantt-chart for Scraii AI.
 * Supplies widgets config, handles floating-panel resize, and passes data in embedded mode.
 */
@Component({
  selector: "app-scraii-gantt-chart",
  templateUrl: "./scraii-gantt-chart.component.html",
  styleUrls: ["./scraii-gantt-chart.component.scss"],
})
export class ScraiiGanttChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() data: any[] = [];
  @Input() programData: any[] = [];
  @Input() config: any = null;
  @Input() isFullscreen = false;
  @Input() isFloatingView = false;
  @Input() isFloatingExpanded = false;
  @Input() chartContainerId?: string;

  @ViewChild(GanttChartComponent) ganttChart?: GanttChartComponent;
  @ViewChild("host", { static: true }) host!: ElementRef<HTMLDivElement>;

  readonly widgetsConfig = SCRAII_GANTT_WIDGETS_CONFIG;

  @HostBinding('class.is-floating-expanded')
  get floatingExpandedClass(): boolean {
    return this.isFloatingView && this.isFloatingExpanded;
  }

  @HostBinding('class.is-fullscreen')
  get fullscreenClass(): boolean {
    return this.isFullscreen;
  }

  private resizeObserver?: ResizeObserver;
  private resizeRaf: number | null = null;
  private viewReady = false;
  private lastObservedWidth = 0;
  private suppressResizeRelayout = false;
  private pendingDataRelayout = false;
  private dataRetryCount = 0;
  private readonly hostClickHandler = (event: MouseEvent) => this.onGanttHostClick(event);

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.host.nativeElement.addEventListener('click', this.hostClickHandler);
    this.lastObservedWidth = this.host.nativeElement.getBoundingClientRect().width;
    this.resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width ?? 0;
      if (this.suppressResizeRelayout || Math.abs(width - this.lastObservedWidth) < 1) {
        return;
      }
      const widthDelta = width - this.lastObservedWidth;
      // A vertical scrollbar shrinks content width slightly — not a real layout resize.
      if (widthDelta < 0 && Math.abs(widthDelta) < 24) {
        this.lastObservedWidth = width;
        return;
      }
      this.lastObservedWidth = width;
      this.scheduleRelayout('width-change');
    });
    this.resizeObserver.observe(this.host.nativeElement);
    this.scheduleRelayout('init');
    if (this.pendingDataRelayout || this.data?.length) {
      this.scheduleRelayout('data-ready');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']?.currentValue?.length) {
      if (!this.viewReady) {
        this.pendingDataRelayout = true;
      } else {
        this.scheduleRelayout('data-ready');
      }
    }
    if (!this.viewReady) {
      return;
    }
    if (
      changes["isFullscreen"] ||
      changes["isFloatingView"] ||
      changes["isFloatingExpanded"]
    ) {
      if (changes["isFullscreen"] && this.ganttChart) {
        this.ganttChart.setPlotReason('fullscreen-toggle');
      }
      this.scheduleRelayout("relayout");
      return;
    }
    if (changes["data"]) {
      const curr = changes["data"].currentValue;
      const prev = changes["data"].previousValue;
      if (curr === prev) {
        return;
      }
      const unchanged =
        Array.isArray(curr) &&
        !!this.ganttChart?.ganttChartData?.length &&
        curr.length === this.ganttChart.ganttChartData.length &&
        curr[0]?.patientid === this.ganttChart.ganttChartData[0]?.patientid;
      if (unchanged) {
        this.syncHostHeightFromChart();
        return;
      }
      // Data updates are handled by app-gantt-chart loadEmbeddedData → applyChartData → plotChart.
      this.syncHostHeightFromChart();
    } else if (changes["programData"]) {
      // Program header rows are applied inside app-gantt-chart; avoid full tree replot.
      this.syncHostHeightFromChart();
    }
  }

  ngOnDestroy(): void {
    this.host?.nativeElement?.removeEventListener('click', this.hostClickHandler);
    this.resizeObserver?.disconnect();
    if (this.resizeRaf !== null) {
      cancelAnimationFrame(this.resizeRaf);
    }
  }

  /** Called by parent when floating panel expand/collapse changes. */
  relayout(): void {
    this.scheduleRelayout("relayout");
  }

  expandAllTree(): void {
    if (!this.ganttChart) {
      return;
    }
    this.ganttChart.expandAllTree();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.syncHostHeightFromChart());
    });
  }

  collapseAllTree(): void {
    if (!this.ganttChart) {
      return;
    }
    this.ganttChart.collapseAllTree();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.syncHostHeightFromChart());
    });
  }

  onChartLayoutChanged(totalHeight?: number): void {
    requestAnimationFrame(() => {
      this.syncHostHeightFromChart(totalHeight, true);
    });
  }

  /** Scraii embedded mode: D3 expand clicks are disabled — delegate from the scroll host. */
  private onGanttHostClick(event: MouseEvent): void {
    const target = event.target as Element | null;
    if (!target?.closest('.expandHitArea, .expandLabel, .expandCircle')) {
      return;
    }
    const group = target.closest('[class*="treeGroup"]');
    if (!group || !this.ganttChart) {
      return;
    }
    const datum = d3.select(group).datum();
    if (!datum) {
      return;
    }
    event.stopPropagation();
    this.ganttChart.triggerTreeExpand(event, datum);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isFullscreen) {
      this.syncHostHeightFromChart();
    }
  }

  private syncHostHeightFromChart(totalHeight?: number, fromChartLayout = false): void {
    const svg = this.host?.nativeElement?.querySelector('svg');
    if (!svg) return;
    const attrHeight = parseFloat(svg.getAttribute('height') || '0');
    const height = Math.ceil(totalHeight || attrHeight || svg.getBoundingClientRect().height);
    const hostEl = this.host.nativeElement;
    const wrapperEl = hostEl.closest('.scraii-ai-chart-wrapper') as HTMLElement | null;
    const containerEl = hostEl.closest('.scraii-ai-gantt-container') as HTMLElement | null;
    const isFullscreen = this.isFullscreen || !!containerEl?.classList.contains('scraii-ai-fullscreen');

    if (isFullscreen) {
      this.suppressResizeRelayout = true;

      // Scrollport: fixed flex height — never set min-height to full SVG content height.
      hostEl.style.minHeight = '0';
      hostEl.style.height = '';
      hostEl.style.maxHeight = '';
      hostEl.style.overflowX = 'auto';
      hostEl.style.overflowY = 'auto';

      const chartHost = hostEl.querySelector('.gantt-chart-host') as HTMLElement | null;
      if (chartHost && height > 0) {
        chartHost.style.minHeight = `${height}px`;
        chartHost.style.height = 'auto';
      }

      if (wrapperEl) {
        wrapperEl.style.minHeight = '0';
        wrapperEl.style.height = '';
        wrapperEl.style.maxHeight = '';
        wrapperEl.style.overflow = 'hidden';
      }
      if (containerEl) {
        containerEl.style.minHeight = '';
        containerEl.style.height = '';
      }
      this.lastObservedWidth = hostEl.getBoundingClientRect().width;
      requestAnimationFrame(() => {
        this.suppressResizeRelayout = false;
      });
      return;
    }

    hostEl.style.maxHeight = '';
    hostEl.style.overflowX = '';
    hostEl.style.overflowY = '';

    if (height > 0) {
      this.suppressResizeRelayout = true;
      hostEl.style.minHeight = `${height}px`;
      hostEl.style.height = 'auto';
      if (wrapperEl) {
        wrapperEl.style.minHeight = `${height}px`;
        wrapperEl.style.height = 'auto';
        wrapperEl.style.maxHeight = 'none';
        wrapperEl.style.overflow = '';
      }
      if (containerEl) {
        containerEl.style.minHeight = `${height + 56}px`;
        containerEl.style.height = 'auto';
      }
      if (fromChartLayout) {
        this.lastObservedWidth = hostEl.getBoundingClientRect().width;
      }
      requestAnimationFrame(() => {
        this.suppressResizeRelayout = false;
      });
    }
  }

  private scheduleRelayout(reason = 'unknown'): void {
    if (this.resizeRaf !== null) {
      cancelAnimationFrame(this.resizeRaf);
    }
    this.resizeRaf = requestAnimationFrame(() => {
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = null;
        const hasInputData = !!this.data?.length;
        const hasChartData = !!this.ganttChart?.ganttChartData?.length;
        if (!hasChartData && hasInputData && this.ganttChart && this.dataRetryCount < 12) {
          this.dataRetryCount += 1;
          this.ganttChart.ensureEmbeddedDataLoaded();
          setTimeout(() => this.scheduleRelayout(reason), 40);
          return;
        }
        this.dataRetryCount = 0;
        this.pendingDataRelayout = false;
        if (hasChartData && this.ganttChart) {
          const shouldPlot =
            reason === "width-change" ||
            reason === "relayout" ||
            reason === "data-ready" ||
            this.ganttChart.lastPlotReason === 'fullscreen-toggle' ||
            (reason === "init" && !this.ganttChart.hasRenderedTree());
          if (shouldPlot) {
            const plotReason =
              this.ganttChart.lastPlotReason === 'fullscreen-toggle' ? 'fullscreen-toggle' : reason;
            this.ganttChart.setPlotReason(plotReason);
            this.ganttChart.plotChart();
            if (plotReason === 'fullscreen-toggle') {
              this.ganttChart.setPlotReason('unknown');
            }
          }
          this.syncHostHeightFromChart();
        }
      });
    });
  }
}
